import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, MoveHorizontal } from "lucide-react";
import { CONTACT } from "../data";
import { useQuote } from "../context/QuoteContext";
import { CompareSlider } from "./CompareSlider";

const lines = ["Professionele dak-", "en gevelreiniging", "met Softwash."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { openQuote } = useQuote();

  return (
    <section
      id="top"
      className="relative overflow-hidden bg-navy-deep grain"
      data-testid="hero"
    >
      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-10 min-h-[100svh] py-28 sm:py-32">
        {/* Left: text */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
          >
            <span className="flex">
              <Check className="h-3.5 w-3.5 text-teal" strokeWidth={3} />
            </span>
            <span className="text-xs font-medium text-white/80">
              Gratis inspectie • Duidelijke offerte • Vakkundige uitvoering
            </span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tight text-white text-4xl sm:text-6xl lg:text-6xl xl:text-7xl leading-[1.02]">
            {lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {i === 2 ? <span className="text-teal">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed"
          >
            SoftPro verwijdert algen, mos, schimmel en hardnekkige vervuiling
            van daken, gevels, terrassen en bedrijfspanden. We werken met lage
            druk en stemmen iedere behandeling zorgvuldig af op de ondergrond.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
          >
            <button
              onClick={() => openQuote()}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-teal px-7 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-1"
            >
              Gratis offerte aanvragen
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-phone"
              className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 font-semibold text-white transition-colors duration-300 hover:bg-white/10"
            >
              <Phone className="h-5 w-5 text-sky" />
              {CONTACT.phone}
            </a>
          </motion.div>
        </div>

        {/* Right: before/after slider */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 hidden rounded-[2rem] bg-white/[0.04] ring-1 ring-white/10 sm:block" />
          <div className="relative">
            <CompareSlider
              item={{ before: "/gevel-before.jpeg", after: "/gevel-after.jpeg", title: "" }}
              alt="Voor en na gevelreiniging van een bakstenen woning in Barneveld"
              testid="hero-slider"
            />
            <p className="mt-4 flex items-center justify-center gap-2 text-sm text-white/60">
              <MoveHorizontal className="h-4 w-4 text-teal" /> Sleep om het
              verschil te zien
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
